import { EmptyValue } from "@/components/ui/record-table/empty-value";
import { Image } from "@/components/ui/image";
import { Package } from "@/components/icons";
import { formatCentsSmart } from "@/lib/formatting/currency";
import {
  getProductTypeBySlug,
  getConditionLabel,
} from "@/lib/generated/product-types";
import type { BrowseListing } from "@/components/listing/browse/marketplace-listing";

export function ListedAtCell({ timestamp }: { timestamp: number | null }) {
  if (!timestamp) return <EmptyValue />;
  const date = new Date(timestamp);
  const now = new Date();
  const diffDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (diffDays === 0)
    return <span className="text-xs text-success-strong font-medium">Today</span>;
  if (diffDays === 1)
    return <span className="text-xs text-muted-foreground">1d ago</span>;
  if (diffDays < 7)
    return (
      <span className="text-xs text-muted-foreground">{diffDays}d ago</span>
    );
  return (
    <span className="text-xs text-muted-foreground">
      {date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
    </span>
  );
}

export function ProductTypeCell({ slug }: { slug: string | undefined }) {
  const productType = slug ? getProductTypeBySlug(slug) : null;
  const label = productType?.pluralDisplayName || slug;
  if (!label) return <EmptyValue />;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-brand-tint text-xs text-brand-strong font-medium">
      {productType?.iconUrl && (
        <Image
          src={productType.iconUrl}
          alt=""
          width={14}
          height={14}
          className="w-3.5 h-3.5 object-contain opacity-70"
        />
      )}
      {label}
    </span>
  );
}

export function ProductImageCell({ product }: { product: BrowseListing }) {
  return (
    <div className="size-9 bg-muted rounded-md overflow-hidden flex-shrink-0 border border-border/50">
      {product.primaryImageUrl ? (
        <Image
          src={product.primaryImageUrl}
          alt={product.title}
          width={36}
          height={36}
          sizes="36px"
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-muted/50">
          <Package className="size-4 text-muted-foreground/50" />
        </div>
      )}
    </div>
  );
}

export function LocationCell({
  city,
  state,
}: {
  city?: string | null;
  state?: string | null;
}) {
  const place = city && state ? `${city}, ${state}` : city || state;
  if (!place) return <EmptyValue />;
  return <span className="block truncate text-foreground">{place}</span>;
}

export function ZipCell({ zip }: { zip?: string | null }) {
  if (!zip) return <EmptyValue />;
  return <span className="tabular-nums text-muted-foreground">{zip}</span>;
}

export function PriceCell({ cents }: { cents: number | null | undefined }) {
  const label =
    typeof cents !== "number"
      ? "Price on request"
      : cents === 0
        ? "Free"
        : formatCentsSmart(cents);

  return (
    <span className="text-sm font-semibold text-foreground tabular-nums">
      {label}
    </span>
  );
}

export function ConditionBadge({
  productTypeSlug,
  conditionValue,
}: {
  productTypeSlug: string | undefined;
  conditionValue: string;
}) {
  const displayLabel = productTypeSlug
    ? getConditionLabel(productTypeSlug, conditionValue)
    : conditionValue;

  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-muted-foreground whitespace-nowrap bg-muted text-muted-foreground">
      {displayLabel}
    </span>
  );
}
